/**
 * Pricing Utilities
 * Helpers for calculating prices with VAT
 */

// Locale to country code mapping
export const LOCALE_TO_COUNTRY: Record<string, string> = {
  'nl': 'NL',
  'de': 'DE',
  'fr': 'FR',
  'en': 'BE', // English defaults to Belgium
};

// Default VAT rates per country (fallback if database not available)
//
// Deze tabel is bewust NIET vervangen door een import van
// `fallbackRatesFor` uit `src/lib/commerce/rates.ts`. Dat bestand importeert
// `@/lib/db` (de Prisma-client) op moduleniveau, en dit bestand wordt onder
// meer geimporteerd door client components (CountryBanner.tsx,
// product-configurator.tsx, GroupedSizeSelector.tsx, popular-products-row.tsx,
// search-dialog.tsx). Een import van de tarievenmodule zou de Prisma-client
// dus meebundelen in de browserbundel, iets waar vandaag geen enkel ander
// pad in de codebase toe leidt (geen `sideEffects: false` in package.json om
// het weg te laten tree-shaken). Zie docs/GELDBEREKENING.md voor de volledige
// afweging. De waarden hier moeten in lijn blijven met
// `FALLBACK_COUNTRY_RATES` in `src/lib/commerce/rates.ts`.
export const DEFAULT_VAT_RATES: Record<string, number> = {
  'NL': 21,
  'BE': 21,
  'DE': 19,
  'FR': 20,
  'DK': 25,
  'ES': 21,
  'IT': 22,
  'LU': 17,
  'PL': 23,
  'AT': 20,
  'CZ': 21,
  'EE': 24,
  'HR': 25,
  'HU': 27,
  'LT': 21,
  'LV': 21,
  'SE': 25,
  'SI': 22,
  'SK': 23,
};

// Countries available per locale
export const LOCALE_SHIPPING_COUNTRIES: Record<string, string[]> = {
  nl: Object.keys(DEFAULT_VAT_RATES),
  de: Object.keys(DEFAULT_VAT_RATES),
  en: Object.keys(DEFAULT_VAT_RATES),
  fr: Object.keys(DEFAULT_VAT_RATES),
};

/**
 * Get country code from locale
 */
export function getCountryFromLocale(locale: string): string {
  return LOCALE_TO_COUNTRY[locale] || 'NL';
}

/**
 * Get VAT rate for a country
 *
 * Blijft de eigen `DEFAULT_VAT_RATES`-tabel gebruiken in plaats van de
 * tarievenmodule (zie het commentaar bij die tabel hierboven): dit bestand
 * wordt ook door client components geimporteerd en mag daarom niets uit
 * `src/lib/commerce/rates.ts` (en daarmee de Prisma-client) meebundelen.
 */
export function getVATRate(countryCode: string): number {
  return DEFAULT_VAT_RATES[countryCode] || 21;
}

/**
 * Calculate price including VAT
 */
export function calculatePriceWithVAT(priceExclVAT: number, countryCode: string): number {
  const vatRate = getVATRate(countryCode);
  return priceExclVAT * (1 + vatRate / 100);
}

export function getDefaultShippingExVAT(specifications: unknown): number {
  const value = (specifications as any)?.fullPriceResponsePrices?.deliveryPromisePrice;
  const shipping = Number(value);
  return Number.isFinite(shipping) ? shipping : 0;
}

export function calculateStorefrontPriceWithVAT(
  basePriceExclVAT: number,
  countryCode: string,
  defaultShippingExclVAT = 0
): number {
  void defaultShippingExclVAT;
  return calculatePriceWithVAT(basePriceExclVAT, countryCode);
}

/**
 * Calculate VAT amount
 */
export function calculateVATAmount(priceExclVAT: number, countryCode: string): number {
  const vatRate = getVATRate(countryCode);
  return priceExclVAT * (vatRate / 100);
}

/**
 * Brutoprijs van een winkelwagenregel op basis van het ACTUELE land, in
 * plaats van het bevroren tarief van het moment waarop de regel werd
 * toegevoegd.
 *
 * De winkelwagen bewaarde tot nu toe alleen de brutoprijs (incl. btw) van het
 * moment van toevoegen. Wijzigde de klant daarna van land (een ander
 * verzendadres, een opgeslagen adres uit een account, of gewoon de
 * landkeuze in de winkelwagen), dan bleef die brutoprijs staan op het oude
 * tarief, terwijl de server bij het afrekenen altijd het tarief van het
 * bestemmingsland gebruikt. Dat gaf een winkelwagen die iets anders toonde
 * dan er werd afgeschreven.
 *
 * Deze functie lost dat op door, waar bekend, uit te gaan van de prijs
 * EXCLUSIEF btw (die verandert niet bij een landwissel) en de brutoprijs
 * telkens opnieuw te berekenen met het btw-tarief van het huidige land.
 * `priceExVat` ontbreekt bij winkelwagens die al in de browser stonden voor
 * deze wijziging; dan valt de functie terug op de bevroren brutoprijs
 * `frozenGrossPrice`, zodat een oude winkelwagen niet stukgaat maar gewoon
 * het oude (mogelijk verouderde) bedrag toont totdat de klant de regel
 * opnieuw toevoegt.
 *
 * Rondt af op 2 decimalen, net als de rest van de geldberekening in de shop.
 */
export function cartItemDisplayPrice(
  priceExVat: number | null | undefined,
  vatRatePercent: number | null | undefined,
  frozenGrossPrice: number
): number {
  if (typeof priceExVat === 'number' && Number.isFinite(priceExVat)) {
    const rate = Number.isFinite(vatRatePercent) ? Number(vatRatePercent) : 21;
    return grossFromExVat(priceExVat, rate);
  }
  return Number.isFinite(frozenGrossPrice) ? frozenGrossPrice : 0;
}

/**
 * Het brutobedrag van een regel in hele centen: btw over het ONAFGERONDE
 * bedrag exclusief btw, en daarna een keer afronden. `orders/create` rekent
 * precies zo, en dat bedrag gaat naar Stripe.
 *
 * Rond het bedrag exclusief btw dus nooit eerst af op centen. De prijs-API
 * geeft voor 10 flyers 48.87499999999999: eerst afronden geeft
 * 48,87 x 1,21 = 59,13, terwijl de productpagina en Stripe 59,14 rekenen.
 */
export function grossFromExVat(priceExVat: number, vatRatePercent: number): number {
  return Number((priceExVat * (1 + vatRatePercent / 100)).toFixed(2));
}

/**
 * Format price for display
 */
export function formatPrice(price: number, locale: string = 'nl-NL', currency: string = 'EUR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(price);
}

export function getPriceLocale(locale: string): string {
  const lang = locale.split('-')[0];
  if (lang === 'de') return 'de-DE';
  if (lang === 'fr') return 'fr-FR';
  if (lang === 'en') return 'en-BE';
  return 'nl-NL';
}

export function formatPriceForLocale(price: number, locale: string, currency: string = 'EUR'): string {
  return formatPrice(price, getPriceLocale(locale), currency);
}

/**
 * Check if visitor's country is supported on current locale
 */
export function isCountrySupportedOnLocale(visitorCountry: string, currentLocale: string): boolean {
  const supportedCountries = LOCALE_SHIPPING_COUNTRIES[currentLocale] || [];
  return supportedCountries.includes(visitorCountry);
}

/**
 * Get recommended locale for a country
 */
export function getRecommendedLocale(countryCode: string): string {
  // Find the locale that has this country as its primary country
  for (const [locale, country] of Object.entries(LOCALE_TO_COUNTRY)) {
    if (country === countryCode) {
      return locale;
    }
  }
  return 'nl'; // Default to Dutch
}
